import type {
  ListWelloCommentDto,
  ListWelloCommentResponse,
} from '__generated__/api/unauth';

import { UnauthApi } from '.';
import type { COMMENT_TYPE } from './types';
import { COMMENT_SORT_TYPE, COMMENT_STATUS } from './types';

export interface CommentData {
  commentList: Comment[];
  totalCount: number;
  totalRootCount: number;
  visibleTotalCount: number;
}

const normalizedComment = (
  comment: ListWelloCommentDto,
  userId?: number,
) => ({
  contentId: comment.contentId,
  parentNickName: comment.parentNickName,
  commentId: comment.welloCommentId,
  commentIdIdx: comment.contentIdIdx,
  createdAt: comment.dateTime,
  commentType: comment.commentTypeValue,
  isBlocked: comment.blockYN,
  isDisplay:
    comment.commentStatusCd === COMMENT_STATUS.NON_DISPLAY ? false : true,
  isReport: comment.reportYN,
  commentTypeCd: comment.commentTypeCd,
  thumbUpCount: comment.likeCount,
  isDeleted: comment.commentStatusCd === COMMENT_STATUS.DELETED,
  contents: comment.contents,
  nickname: comment.nickName,
  profileImage: comment.profileImage,
  isMine: comment.createBy === userId,
  isMyThumbUp: comment.likeYN,
  parentCommentId: comment.parentWelloCommentId,
  rootCommentId: comment.rootWelloCommentId,
  isBest: comment.bestYN,
  isPicked: comment.commentPickYN,
  isWelloHelper: comment.welloHelperYN,
  relationPolicyList: comment.relationPolicyList,
  hasRelativePolicy: !!comment.relationPolicyList?.length,
});

type NormalizedComment = ReturnType<typeof normalizedComment>;

export interface Comment extends NormalizedComment {
  childCommentList?: NormalizedComment[];
}

export const selectCommentList = (
  context?: Partial<ListWelloCommentResponse>,
  userId?: number,
): CommentData => {
  if (!context || !context.contents) {
    return {
      commentList: [],
      totalCount: 0,
      totalRootCount: 0,
      visibleTotalCount: 0,
    };
  }
  const classifyCommentList = (commentList: ListWelloCommentDto[]) =>
    commentList.reduce((acc, comment) => {
      const transformedComment = normalizedComment(comment, userId);

      if (!comment.rootWelloCommentId) {
        acc.push({ ...transformedComment, childCommentList: [] });
      } else {
        const rootComment = acc.find(
          ({ commentId }) => commentId === comment.rootWelloCommentId,
        );
        rootComment?.childCommentList?.push(transformedComment);
      }

      return acc;
    }, [] as Comment[]);

  const commentList = classifyCommentList(context.contents);

  const specificCommentList = context.specificComment
    ? classifyCommentList(
        context.specificComment.map(
          ({ parentNickName: parent_nick_name = '', ...commonValues }) => ({
            commentCount: 0,
            parentNickName: parent_nick_name,
            ...commonValues,
          }),
        ),
      )
    : [];

  return {
    commentList: specificCommentList.concat(commentList),
    totalRootCount: context.rootTotalCount ?? 0,
    totalCount: context.totalCount ?? 0,
    visibleTotalCount: context.visibleTotalCount ?? 0,
  };
};

// 정책카드는 UUID(string), 그 외 컨텐츠는 숫자 ID. 백엔드가 두 필드를 분리해서 받는다.
export const toContentIdQuery = (id: number | string) =>
  typeof id === 'string' ? { contentUuid: id } : { contentId: id };

interface GetCommentListProps {
  id: number | string;
  commentType: COMMENT_TYPE;
  sortType?: COMMENT_SORT_TYPE;
}

export const getCommentList = async ({
  commentType,
  id,
}: GetCommentListProps) => {
  const { data } = await UnauthApi['get:/wello/v2/allows/wello-comment']({
    query: {
      page: 1,
      size: 100,
      commentTypeCd: commentType,
      commentSort: COMMENT_SORT_TYPE.LATEST,
      ...toContentIdQuery(id),
    },
  });

  const context = data?.context;

  return selectCommentList(context);
};
